import React from 'react';
import SelectField from '../common/SelectField.js';
import {

  CORE_FEATURES,
  AGENT_NETWORK_FEATURES,
  formatFeatureLabel,
  getAgentNetworkCanAccessReset,
} from '../../utils/companyFeatures.js';

const FeatureCheckbox = ({ feature, checked, onChange, disabled, hint }) => (
  <div className={`flex items-start space-x-3 p-3 border border-gray-200 rounded-xl ${disabled ? 'opacity-50 bg-gray-50' : 'hover:bg-gray-50'}`}>
    <input
      type="checkbox"
      id={feature}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="w-4 h-4 mt-0.5 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
    />
    <div>
      <label htmlFor={feature} className={`text-xs sm:text-sm text-gray-900 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        {formatFeatureLabel(feature)}
      </label>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const AttendanceMethodCheckbox = ({ id, label, hint, checked, onChange, disabled }) => (
  <label
    htmlFor={id}
    className={`flex items-start gap-2 p-2 rounded-lg border ${
      disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-white border-gray-200 bg-white'
    }`}
  >
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="w-4 h-4 mt-0.5 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
    />
    <span>
      <span className="block text-xs sm:text-sm text-gray-800">{label}</span>
      <span className="block text-[11px] text-gray-500">{hint}</span>
    </span>
  </label>
);

const FeatureAccessFields = ({ formData, onNetworkModeChange, onFeatureToggle }) => {
  const isExternal = formData.network_mode === 'EXTERNAL';
  const agentNetworkEnabled = Boolean(formData.can_access.agent_network);
  const attendanceEnabled = !isExternal && Boolean(formData.can_access.attendance);

  return (
    <>
      <div>
        <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
          Agent Network Mode
        </h3>
        <div className="max-w-md space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
            Operation Mode
          </label>
          <SelectField variant="form"
            name="network_mode"
            value={formData.network_mode}
            onChange={onNetworkModeChange}
            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          >
            <option value="INTERNAL">Company Owner (default — existing flow)</option>
            <option value="EXTERNAL">Agent (cross-company agent network)</option>
          </SelectField>
          <p className="text-xs text-gray-500">
            Agent mode enables cross-company agent partnerships, receiver agent booking, and network shipment views.
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
          Core Feature Access
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {CORE_FEATURES.map((feature) => {
            const isAttendanceDisabled = isExternal && feature === 'attendance';
            let hint;
            if (isAttendanceDisabled) {
              hint = 'Not available in Agent network mode';
            } else if (feature === 'attendance') {
              hint = 'Enable, then choose Manual (header), QR (branch scan), or both.';
            } else if (feature === 'my_partnerships') {
              hint = 'Allow Agents to see My Partnerships and accept/reject partnership requests';
            } else if (feature === 'commission' && !isExternal) {
              hint = 'ML agent commission on freight (Agent Commission, deduct on To Pay settlement). Paid Amount Settlement still applies for Paid collections.';
            } else if (feature === 'login_otp') {
              hint = 'After email and password, send a WhatsApp OTP to the login user\'s contact. WhatsApp Notification must also be enabled.';
            }

            if (feature === 'attendance') {
              return (
                <div
                  key={feature}
                  className={`p-3 border border-gray-200 rounded-xl space-y-2 ${
                    isAttendanceDisabled ? 'opacity-50 bg-gray-50' : 'hover:bg-gray-50'
                  } ${attendanceEnabled ? 'col-span-2 lg:col-span-1' : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={feature}
                      checked={isAttendanceDisabled ? false : Boolean(formData.can_access.attendance)}
                      onChange={() => onFeatureToggle(feature)}
                      disabled={isAttendanceDisabled}
                      className="w-4 h-4 mt-0.5 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
                    />
                    <div>
                      <label htmlFor={feature} className={`text-xs sm:text-sm text-gray-900 ${isAttendanceDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        {formatFeatureLabel(feature)}
                      </label>
                      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
                    </div>
                  </div>
                  {attendanceEnabled && (
                    <div className="space-y-2 pl-7">
                      <AttendanceMethodCheckbox
                        id="attendance_manual"
                        label="Manual"
                        hint="Header clock-in icon"
                        checked={Boolean(formData.can_access.attendance_manual)}
                        onChange={() => onFeatureToggle('attendance_manual')}
                      />
                      <AttendanceMethodCheckbox
                        id="attendance_qr"
                        label="QR"
                        hint="Branch QR in Scan modal"
                        checked={Boolean(formData.can_access.attendance_qr)}
                        onChange={() => onFeatureToggle('attendance_qr')}
                      />
                    </div>
                  )}
                </div>
              );
            }

            return (
              <FeatureCheckbox
                key={feature}
                feature={feature}
                checked={isAttendanceDisabled ? false : formData.can_access[feature]}
                onChange={() => onFeatureToggle(feature)}
                disabled={isAttendanceDisabled}
                hint={hint}
              />
            );
          })}
        </div>
      </div>

      {isExternal && (
        <div>
          <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
            Agent Network Features
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-3">
            Enable Agent Network first, then configure which external capabilities this company can use.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {AGENT_NETWORK_FEATURES.map((feature) => {
              const isAgentNetworkMaster = feature === 'agent_network';
              const disabled = !isAgentNetworkMaster && !agentNetworkEnabled;
              return (
                <FeatureCheckbox
                  key={feature}
                  feature={feature}
                  checked={formData.can_access[feature]}
                  onChange={() => onFeatureToggle(feature)}
                  disabled={disabled}
                  hint={
                    isAgentNetworkMaster
                      ? 'Required to show Agent Network menu and cross-company flows'
                      : disabled
                        ? 'Enable Agent Network first'
                        : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export { getAgentNetworkCanAccessReset };
export default FeatureAccessFields;
